import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Search } from 'lucide-react';
import { apiRequest, formatDate, truncateText } from '../lib/utils';
import { Input } from '../components/ui/input';

// Default blog data in case API returns empty
const DEFAULT_BLOGS = [
  {
    id: '1',
    title: 'What Is a Denturist? (And How Is It Different from a Dentist?)',
    slug: 'what-is-a-denturist',
    excerpt: 'A Denturist is a licensed oral health professional who specializes exclusively in the design, fabrication, fitting, and maintenance of removable dental prosthetics.',
    featured_image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600',
    created_at: '2024-01-15T10:00:00Z',
    published: true
  },
  {
    id: '2',
    title: 'Why Insurance Companies Support Direct Denturist Care',
    slug: 'insurance-supports-direct-care',
    excerpt: 'Insurance carriers increasingly recognize that direct denturist care reduces unnecessary layered costs.',
    featured_image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600',
    created_at: '2024-01-12T10:00:00Z',
    published: true
  },
  {
    id: '3',
    title: 'The True Cost Savings of Choosing a Denturist',
    slug: 'cost-savings-denturist',
    excerpt: 'Patients frequently ask why pricing differs between general dental offices and denturist practices.',
    featured_image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600',
    created_at: '2024-01-10T10:00:00Z',
    published: true
  },
  {
    id: '4',
    title: 'How Dentures Restore Confidence and Oral Function',
    slug: 'dentures-restore-confidence',
    excerpt: 'Well-fitted dentures restore chewing efficiency, speech clarity, facial structure support, and self-confidence.',
    featured_image: 'https://images.unsplash.com/photo-1580483046931-aaba29b81601?w=600',
    created_at: '2024-01-08T10:00:00Z',
    published: true
  },
  {
    id: '5',
    title: 'The Denture Process Step-by-Step',
    slug: 'denture-process-step-by-step',
    excerpt: 'Understanding the denture fabrication process from consultation to final fitting.',
    featured_image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=600',
    created_at: '2024-01-06T10:00:00Z',
    published: true
  },
  {
    id: '6',
    title: 'Common Myths About Dentures',
    slug: 'common-denture-myths',
    excerpt: 'Debunking common misconceptions about modern dentures and what patients can really expect.',
    featured_image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=600',
    created_at: '2024-01-04T10:00:00Z',
    published: true
  },
  {
    id: '7',
    title: 'Why Oregon Patients Trust Our Practice',
    slug: 'why-oregon-patients-trust-us',
    excerpt: 'As Oregon\'s longest-standing denturist practice, our longevity reflects quality, ethics, and patient satisfaction.',
    featured_image: 'https://images.pexels.com/photos/35438289/pexels-photo-35438289.jpeg?w=600',
    created_at: '2024-01-02T10:00:00Z',
    published: true
  },
  {
    id: '8',
    title: 'When to Replace or Reline Dentures',
    slug: 'when-to-replace-reline-dentures',
    excerpt: 'Signs you may need relining or replacement include slipping, sore spots, and difficulty chewing.',
    featured_image: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?w=600',
    created_at: '2024-01-01T10:00:00Z',
    published: true
  },
];

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiRequest('/public/blogs')
      .then((data) => {
        if (data && data.length > 0) {
          setBlogs(data);
        } else {
          setBlogs(DEFAULT_BLOGS);
        }
      })
      .catch(() => setBlogs(DEFAULT_BLOGS))
      .finally(() => setLoading(false));
  }, []);

  const filteredBlogs = blogs.filter(blog =>
    blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (blog.excerpt && blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const featuredBlog = filteredBlogs[0];
  const otherBlogs = filteredBlogs.slice(1);

  return (
    <div className="pt-24 lg:pt-32" data-testid="blog-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Blog & Resources
            </h1>
            <p className="mt-6 text-lg text-slate-600">
              Expert insights, patient education, and helpful information about dentures and oral health.
            </p>
            
            {/* Search */}
            <div className="relative mt-8 max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 rounded-full bg-white"
                data-testid="blog-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 lg:py-24 bg-white" data-testid="blog-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500">No articles found matching your search.</p>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {featuredBlog && (
                <Link
                  to={`/blog/${featuredBlog.slug}`}
                  className="block mb-16 group"
                  data-testid="featured-blog"
                >
                  <div className="grid lg:grid-cols-2 gap-8 items-center">
                    <div className="relative overflow-hidden rounded-3xl aspect-[16/10]">
                      <img
                        src={featuredBlog.featured_image || 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800'}
                        alt={featuredBlog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4 bg-orange-600 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                        Featured
                      </div>
                    </div>
                    <div className="lg:pl-8">
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(featuredBlog.created_at)}</span>
                      </div>
                      <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 group-hover:text-orange-600 transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {featuredBlog.title}
                      </h2>
                      <p className="mt-4 text-slate-600 leading-relaxed">
                        {featuredBlog.excerpt || truncateText(featuredBlog.content || '', 200)}
                      </p>
                      <div className="mt-6 inline-flex items-center text-orange-600 font-medium group-hover:gap-3 gap-2 transition-all">
                        Read More <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Other Posts */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {otherBlogs.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/blog/${blog.slug}`}
                    className="group blog-card"
                    data-testid={`blog-card-${blog.slug}`}
                  >
                    <div className="relative overflow-hidden rounded-t-2xl aspect-[16/10]">
                      <img
                        src={blog.featured_image || 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600'}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(blog.created_at)}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 group-hover:text-orange-600 transition-colors line-clamp-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {blog.title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                        {blog.excerpt || truncateText(blog.content || '', 100)}
                      </p>
                      <div className="mt-4 inline-flex items-center text-orange-600 text-sm font-medium">
                        Read More <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
